import React, { useState, useCallback } from 'react';
import apiClient from '../../services/api';
import ExplorerControls from './ExplorerControls';
import ExplorerFilters from './ExplorerFilters';
import ExplorerScatterPlot from './ExplorerScatterPlot';
import ExplorerPinnedStrip from './ExplorerPinnedStrip';
import ExplorerHeatmap from './ExplorerHeatmap';

function ExplorerTab({ tickers, onNavigateToInput }) {
  // Data state
  const [explorerData, setExplorerData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [loadingMessage, setLoadingMessage] = useState('');
  const [error, setError] = useState(null);

  // Scenario slider state (controls scatter Y-axis)
  const [currentScenario, setCurrentScenario] = useState(20);

  // Filter state
  const [activeTickers, setActiveTickers] = useState(new Set());
  const [activeExpBuckets, setActiveExpBuckets] = useState(new Set());
  const [activeMoneynessLabels, setActiveMoneynessLabels] = useState(new Set());
  const [activeScenarios, setActiveScenarios] = useState(new Set());

  // Pinned contracts (max 8)
  const [pinnedContracts, setPinnedContracts] = useState([]);

  const initializeFilters = useCallback((contracts) => {
    const tickers = new Set(contracts.map(c => c.ticker));
    const expBuckets = new Set(contracts.map(c => c.exp_bucket));
    const moneyness = new Set(contracts.map(c => c.moneyness_bucket));
    const scenarios = new Set([
      '-50', '-40', '-30', '-20', '-15', '-10', '-5', '0',
      '5', '10', '15', '20', '25', '30', '40', '50', '75', '100'
    ]);

    setActiveTickers(tickers);
    setActiveExpBuckets(expBuckets);
    setActiveMoneynessLabels(moneyness);
    setActiveScenarios(scenarios);
  }, []);

  const handleGenerate = async (params) => {
    setLoading(true);
    setError(null);
    setPinnedContracts([]);
    setLoadingMessage(`Fetching options chains for ${params.tickers.join(', ')}...`);

    try {
      const response = await apiClient.post('/stock-analyzer/explorer/', params);
      const data = response.data;

      setExplorerData(data);
      initializeFilters(data.contracts || []);

      if ((data.contracts || []).length === 0) {
        setError('No contracts found matching the grid criteria.');
      }
    } catch (err) {
      console.error('Explorer fetch failed:', err);
      const msg = err.response?.data?.error || 'Failed to fetch explorer data';
      setError(msg);
    } finally {
      setLoading(false);
      setLoadingMessage('');
    }
  };

  const handleTogglePin = (contract) => {
    setPinnedContracts(prev => {
      const key = `${contract.ticker}_${contract.strike}_${contract.expiration}`;
      const exists = prev.some(
        p => `${p.ticker}_${p.strike}_${p.expiration}` === key
      );

      if (exists) {
        return prev.filter(
          p => `${p.ticker}_${p.strike}_${p.expiration}` !== key
        );
      }

      if (prev.length >= 8) return prev;
      return [...prev, contract];
    });
  };

  const handleUnpin = (contract) => {
    setPinnedContracts(prev =>
      prev.filter(
        p => !(p.ticker === contract.ticker && p.strike === contract.strike && p.expiration === contract.expiration)
      )
    );
  };

  const isPinned = (contract) => {
    return pinnedContracts.some(
      p => p.ticker === contract.ticker && p.strike === contract.strike && p.expiration === contract.expiration
    );
  };

  // Apply filters to contracts
  const filteredContracts = explorerData?.contracts?.filter(c => {
    if (!activeTickers.has(c.ticker)) return false;
    if (!activeExpBuckets.has(c.exp_bucket)) return false;
    if (!activeMoneynessLabels.has(c.moneyness_bucket)) return false;
    return true;
  }) || [];

  // No tickers available
  if (!tickers || tickers.length === 0) {
    return (
      <div className="text-center py-16">
        <div className="text-6xl mb-4">🔭</div>
        <h2 className="text-xl font-semibold text-white mb-2">Options Explorer</h2>
        <p className="text-gray-400 mb-6">
          Run an analysis from the Input tab to populate tickers, then explore options contracts here.
        </p>
        <button
          onClick={onNavigateToInput}
          className="px-6 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 transition-colors"
        >
          Go to Analysis Input
        </button>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div>
        <h2 className="text-xl font-semibold text-white mb-1">Options Explorer</h2>
        <p className="text-gray-400 text-sm">
          Scan contracts across expirations and strikes. Use the scatter plot to discover, then pin contracts for detailed heatmap analysis.
        </p>
      </div>

      {/* Controls */}
      <ExplorerControls
        tickers={tickers}
        onGenerate={handleGenerate}
        loading={loading}
      />

      {/* Loading */}
      {loading && (
        <div className="flex items-center justify-center py-12">
          <div className="text-center">
            <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-blue-500 mx-auto mb-4"></div>
            <p className="text-gray-400">{loadingMessage}</p>
          </div>
        </div>
      )}

      {/* Error */}
      {error && (
        <div className="bg-red-900/20 border border-red-700 rounded-md p-4">
          <p className="text-red-400">{error}</p>
        </div>
      )}

      {/* Results */}
      {explorerData && !loading && (
        <>
          {/* Ticker summary */}
          <div className="flex flex-wrap gap-3 text-sm">
            {Object.entries(explorerData.tickers_data || {}).map(([ticker, data]) => (
              <span key={ticker} className="px-3 py-1 bg-gray-700 rounded-full text-gray-300">
                {ticker}: ${data.underlying_price?.toFixed(2)} ({data.contracts_fetched} fetched)
              </span>
            ))}
            <span className="px-3 py-1 bg-blue-900/30 rounded-full text-blue-400">
              {filteredContracts.length} contracts on grid
            </span>
          </div>

          {/* Filters */}
          <ExplorerFilters
            contracts={explorerData.contracts || []}
            activeTickers={activeTickers}
            setActiveTickers={setActiveTickers}
            activeExpBuckets={activeExpBuckets}
            setActiveExpBuckets={setActiveExpBuckets}
            activeMoneynessLabels={activeMoneynessLabels}
            setActiveMoneynessLabels={setActiveMoneynessLabels}
            activeScenarios={activeScenarios}
            setActiveScenarios={setActiveScenarios}
          />

          {/* Scenario Slider */}
          <div className="bg-gray-800 rounded-lg px-4 py-3">
            <div className="flex items-center justify-between mb-2">
              <label className="text-xs font-medium text-gray-300">
                Scatter Plot Scenario (Y-Axis)
              </label>
              <span className="text-sm font-bold text-blue-500">
                {currentScenario > 0 ? '+' : ''}{currentScenario}% underlying move
              </span>
            </div>
            <input
              type="range"
              min="-50"
              max="100"
              value={currentScenario}
              onChange={(e) => setCurrentScenario(Number(e.target.value))}
              className="w-full h-2 bg-gray-700 rounded-lg appearance-none cursor-pointer"
              style={{
                background: `linear-gradient(to right, #218aff 0%, #218aff ${
                  ((currentScenario + 50) / 150) * 100
                }%, #374151 ${((currentScenario + 50) / 150) * 100}%, #374151 100%)`
              }}
            />
            <div className="flex justify-between text-xs text-gray-500 mt-1">
              <span>-50%</span>
              <span>0%</span>
              <span>+100%</span>
            </div>
            {/* Hot buttons */}
            <div className="flex flex-wrap gap-1 mt-2">
              {[-50, -35, -20, -10, 10, 20, 35, 50, 100].map(val => (
                <button
                  key={val}
                  onClick={() => setCurrentScenario(val)}
                  className={`px-2 py-1 text-xs rounded transition-colors ${
                    currentScenario === val
                      ? 'bg-blue-600 text-white'
                      : 'bg-gray-700 text-gray-300 hover:bg-gray-600'
                  }`}
                >
                  {val > 0 ? '+' : ''}{val}%
                </button>
              ))}
            </div>
          </div>

          {/* Scatter Plot */}
          <ExplorerScatterPlot
            contracts={filteredContracts}
            currentScenario={currentScenario}
            pinnedContracts={pinnedContracts}
            onTogglePin={handleTogglePin}
            isPinned={isPinned}
          />

          {/* Pinned Strip */}
          {pinnedContracts.length > 0 && (
            <ExplorerPinnedStrip
              contracts={pinnedContracts}
              currentScenario={currentScenario}
              onUnpin={handleUnpin}
            />
          )}

          {/* Heatmap */}
          {pinnedContracts.length > 0 && (
            <ExplorerHeatmap
              contracts={pinnedContracts}
              activeScenarios={activeScenarios}
            />
          )}
        </>
      )}

      {/* Slider styles */}
      <style>{`
        input[type="range"]::-webkit-slider-thumb {
          appearance: none;
          width: 16px;
          height: 16px;
          background: #218aff;
          cursor: pointer;
          border-radius: 50%;
          border: 2px solid #1f2937;
        }
        input[type="range"]::-moz-range-thumb {
          width: 16px;
          height: 16px;
          background: #218aff;
          cursor: pointer;
          border-radius: 50%;
          border: 2px solid #1f2937;
        }
      `}</style>
    </div>
  );
}

export default ExplorerTab;
